import { ChevronDown, ChevronUp } from 'lucide-react';
import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import './StripeTable.css';

const StripeTable = ({
  columns = [],
  data = [],
  sortColumn = null,
  sortDirection = 'asc',
  onSort,
  selectable = false,
  selectedRows = [],
  onSelectRow,
  onSelectAll,
  actions,
  emptyMessage = 'No data available',
  className = '',
}) => {
  const handleSort = useCallback(
    (column) => {
      if (onSort && column.sortable !== false) {
        onSort(column.key);
      }
    },
    [onSort]
  );

  const handleSelectAll = useCallback(
    (e) => {
      if (onSelectAll) {
        onSelectAll(e.target.checked);
      }
    },
    [onSelectAll]
  );

  const handleSelectRow = useCallback(
    (row, e) => {
      if (onSelectRow) {
        onSelectRow(row, e.target.checked);
      }
    },
    [onSelectRow]
  );

  const createSortHandler = useCallback(
    (column) => {
      return () => handleSort(column);
    },
    [handleSort]
  );

  const createSelectRowHandler = useCallback(
    (row) => {
      return (e) => handleSelectRow(row, e);
    },
    [handleSelectRow]
  );

  const renderCell = (row, column) => {
    if (column.render) {
      return column.render(row[column.key], row);
    }
    return row[column.key];
  };

  const isRowSelected = (row) => {
    return selectedRows.some((selected) => selected === row || selected.id === row.id);
  };

  const allRowsSelected = data.length > 0 && selectedRows.length === data.length;

  return (
    <div className={`stripe-table-container ${className}`}>
      <table className="stripe-table">
        <thead>
          <tr>
            {selectable && (
              <th className="stripe-checkbox-column">
                <input
                  type="checkbox"
                  onChange={handleSelectAll}
                  checked={allRowsSelected}
                  className="stripe-checkbox"
                />
              </th>
            )}
            {columns.map((column) => {
              const isSortable = column.sortable !== false;
              return (
                <th
                  key={column.key}
                  className={`${isSortable ? 'sortable' : ''} ${
                    column.align ? `text-${column.align}` : ''
                  }`}
                  onClick={isSortable ? createSortHandler(column) : undefined}
                >
                  <div className="stripe-th-content">
                    <span>{column.label}</span>
                    {isSortable && sortColumn === column.key && (
                      <span className="stripe-sort-icon">
                        {sortDirection === 'asc' ? (
                          <ChevronUp size={14} />
                        ) : (
                          <ChevronDown size={14} />
                        )}
                      </span>
                    )}
                  </div>
                </th>
              );
            })}
            {actions && <th className="stripe-actions-column">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length + (selectable ? 1 : 0) + (actions ? 1 : 0)}
                className="stripe-empty-message"
              >
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((row, index) => (
              <tr key={row.id || index}>
                {selectable && (
                  <td className="stripe-checkbox-column">
                    <input
                      type="checkbox"
                      checked={isRowSelected(row)}
                      onChange={createSelectRowHandler(row)}
                      className="stripe-checkbox"
                    />
                  </td>
                )}
                {columns.map((column) => (
                  <td key={column.key} className={column.align ? `text-${column.align}` : ''}>
                    {renderCell(row, column)}
                  </td>
                ))}
                {actions && <td className="stripe-actions-column">{actions(row)}</td>}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

StripeTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      sortable: PropTypes.bool,
      align: PropTypes.oneOf(['left', 'center', 'right']),
      render: PropTypes.func,
    })
  ).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  sortColumn: PropTypes.string,
  sortDirection: PropTypes.oneOf(['asc', 'desc']),
  onSort: PropTypes.func,
  selectable: PropTypes.bool,
  selectedRows: PropTypes.array,
  onSelectRow: PropTypes.func,
  onSelectAll: PropTypes.func,
  actions: PropTypes.func,
  emptyMessage: PropTypes.string,
  className: PropTypes.string,
};

export default StripeTable;

